// PlanSettingsPanel.jsx — DAS-3026
//
// The nineteen numbers the morning advice reads: thirteen plan numbers (DAS-3020,
// User_Plan_Parameters) and the box 3 tax rates with their year (DAS-2810 /
// DAS-3024, User_Tax_Context). Mounted under the Investment Strategy editor in the
// Account menu: the document stays the story, these rows are what the code reads.
// Same auth style as its neighbour (raw fetch with the stored token, so the /api
// prefix IS included — DAS-2550: raw fetch includes /api, the shared axios
// instance does not). The server validates every field against an allowlist.

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d - new Date()) / 86400000);
};

const PlanSettingsPanel = () => {
  const [data, setData] = useState(null);       // { plan, tax, fields }
  const [plan, setPlan] = useState({});
  const [rates, setRates] = useState([]);       // [{label, value}]
  const [year, setYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/plan-settings', { headers: authHeaders() });
      if (!res.ok) throw new Error('Could not load the plan and tax numbers');
      const d = await res.json();
      setData(d);
      setPlan(d.plan || {});
      setRates(d.tax?.rates || []);
      setYear(d.tax?.valid_for_year ?? '');
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      setSaving(true); setSaved(false);
      // plan state carries read-only columns straight from the GET response
      // (updated_at, cpb_fetched_at, ...) that aren't in PLAN_FIELDS, so sending
      // them back trips the backend's allowlist and 400s the whole save (DAS-3026,
      // DAS-3338). Only send fields the panel actually renders (data.fields, built
      // from PLAN_FIELDS) so a future read-only column can't break Save again.
      const knownFields = new Set(fields.map((f) => f.field));
      const planToSave = Object.fromEntries(Object.entries(plan).filter(([k]) => knownFields.has(k)));
      const body = { plan: planToSave, tax: { valid_for_year: year, rates } };
      const res = await fetch('/api/plan-settings', { method: 'PUT', headers: authHeaders(), body: JSON.stringify(body) });
      const d = await res.json();
      if (!res.ok) throw new Error((d.details || [d.error || 'save failed']).join('; '));
      setData(d); setPlan(d.plan || {}); setRates(d.tax?.rates || []); setYear(d.tax?.valid_for_year ?? '');
      setError(null); setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>;

  const fields = data?.fields || [];
  const days = daysUntil(plan.restructure_deadline);
  const thisYear = new Date().getFullYear();
  const stale = year && Number(year) !== thisYear;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }} data-testid="plan-settings-panel">
      <Typography variant="h6" gutterBottom>Plan and tax numbers</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        The document above is the story. These are the numbers the morning advice actually reads every day: the plan check,
        the four questions, the cap, the tax block. Change a number here and tomorrow's advice uses it.
        {data?.plan?.updated_at ? ` Last changed ${String(data.plan.updated_at).replace('T', ' ').slice(0, 16)}.` : ''}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && !error && <Alert severity="success" sx={{ mb: 2 }}>Saved. Tomorrow's 06:17 run uses these numbers.</Alert>}

      <Grid container spacing={2}>
        {fields.map((f) => (
          <Grid item xs={12} md={6} key={f.field}>
            {f.kind === 'cap_base' ? (
              <TextField select fullWidth size="small" label={f.label} value={plan[f.field] || 'securities'}
                         onChange={(e) => setPlan({ ...plan, [f.field]: e.target.value })} helperText={f.help}
                         inputProps={{ 'data-testid': `plan-field-${f.field}` }}>
                <MenuItem value="securities">securities book</MenuItem>
                <MenuItem value="investable">investable assets (securities + savings)</MenuItem>
              </TextField>
            ) : (
              <TextField fullWidth size="small" label={f.label} helperText={f.help}
                         type={f.kind === 'date' ? 'date' : 'number'}
                         value={plan[f.field] ?? ''}
                         onChange={(e) => setPlan({ ...plan, [f.field]: e.target.value })}
                         InputLabelProps={f.kind === 'date' ? { shrink: true } : undefined}
                         inputProps={{ 'data-testid': `plan-field-${f.field}`, step: f.kind === 'pct' ? 0.1 : 1 }} />
            )}
          </Grid>
        ))}
      </Grid>
      {days !== null && (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }} data-testid="plan-deadline-days">
          {days >= 0 ? `${days} days of tax-free selling left.` : `The tax-free selling window closed ${-days} days ago.`}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom>Box 3 tax rates</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        Your own working from the strategy document, not adviser-confirmed; the advice says so when it quotes them.
        Refresh every January: the advice flags them as stale after New Year.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField fullWidth size="small" type="number" label="Rates valid for year" value={year}
                     onChange={(e) => setYear(e.target.value)} inputProps={{ 'data-testid': 'tax-valid-year' }}
                     helperText={stale ? `Stale: it is ${thisYear}.` : 'Current.'} error={Boolean(stale)} />
        </Grid>
        {rates.map((r, i) => (
          <Grid item xs={12} md={6} key={i} sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="Rate" value={r.label} sx={{ flex: 2 }}
                       onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            <TextField size="small" label="Value" value={r.value} sx={{ flex: 1 }}
                       onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
            <Button size="small" onClick={() => setRates(rates.filter((_, j) => j !== i))}>remove</Button>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Button size="small" onClick={() => setRates([...rates, { label: '', value: '' }])} disabled={rates.length >= 12}>add a rate</Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving} data-testid="plan-settings-save">
          {saving ? 'Saving…' : 'Save the numbers'}
        </Button>
      </Box>
    </Paper>
  );
};

export default PlanSettingsPanel;
